import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Car, User, Hammer, FileText, Receipt, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

const ICON = { vehicle: Car, customer: User, job: Hammer, quotation: FileText, invoice: Receipt };
const LABEL = { vehicle: "Vehicle", customer: "Customer", job: "Job", quotation: "Quotation", invoice: "Invoice" };

/** Universal search bar — VRM / name / phone / make / model / VIN / job# / quote# / invoice#. */
export function UniversalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const text = q.trim();
    if (text.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try { const r = await api(`/search?q=${encodeURIComponent(text)}`); setResults(r.results || []); setOpen(true); }
      catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const go = (r) => { setOpen(false); setQ(""); navigate(r.link); };

  return (
    <div ref={boxRef} className="relative mb-6" data-testid="universal-search">
      <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 shadow-sm focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
        <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
        <input
          data-testid="universal-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Search reg, customer, phone, make/model, VIN, job / quote / invoice no…"
          className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        {!!q && !loading && <button onClick={() => { setQ(""); setResults([]); }} data-testid="universal-search-clear"><X className="h-4 w-4 text-muted-foreground" /></button>}
      </div>

      {open && (
        <div className="absolute z-30 mt-2 max-h-[60vh] w-full overflow-auto rounded-xl border border-border bg-card p-1.5 shadow-xl" data-testid="universal-search-results">
          {results.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">No matches for “{q.trim()}”.</p>
          ) : results.map((r) => {
            const Icon = ICON[r.type] || Search;
            return (
              <button key={`${r.type}-${r.id}`} onClick={() => go(r)} data-testid={`search-result-${r.type}`}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-secondary">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-tertiary text-brand"><Icon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-midnight">{r.label}</span>
                  {r.sublabel ? <span className="block truncate text-xs text-muted-foreground">{r.sublabel}</span> : null}
                </span>
                <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{LABEL[r.type] || r.type}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
